import type { CompanyDiscoveryAiOutput } from '@revelio/data-access';

import { getFilterDefinition } from '../../engine/filterRegistry';
import type {
  Filter,
  ListFilter,
  RangeFilter,
  SegmentedFilter,
  TreeFilter,
  TreeSelection,
} from '../../types/filters';
import { FilterId, FilterType } from '../../types/filters';
import {
  getFilterIdForSegmentField,
  RATE_SEGMENT_FILTER_IDS,
  segmentFilterKeyToFilterId,
} from './screenerMappings';

type SegmentItem = NonNullable<
  NonNullable<
    CompanyDiscoveryAiOutput['filters']['segments']['headcount']
  >[number]
>;
type SegmentPeopleFilter = SegmentItem['filter'];

type ResolvedPromptItem = {
  name: string;
  relevantPromptText: string;
  id?: string;
  levelId?: string;
  selectionListId?: string;
};

function buildSegmentationsFromResolvedFilter(
  filter: SegmentPeopleFilter
): Partial<Record<string, Filter>> {
  const segmentations: Partial<Record<string, Filter>> = {};
  if (!filter || typeof filter !== 'object') return segmentations;

  const keys: (keyof SegmentPeopleFilter)[] = [
    'geography',
    'industry',
    'role',
    'seniority',
    'highest_degree',
    'gender',
    'ethnicity',
  ];

  for (const key of keys) {
    const arr = filter[key];
    if (!Array.isArray(arr) || arr.length === 0) continue;

    const mapping = segmentFilterKeyToFilterId[key];
    if (!mapping) continue;

    const items = arr as ResolvedPromptItem[];

    if (mapping.type === 'tree') {
      const definition = getFilterDefinition(mapping.filterId);
      if (!definition || definition.type !== FilterType.TREE) continue;
      const selectionListIds =
        (definition as { selectionListIds?: string[] }).selectionListIds ?? [];
      const selections: TreeFilter['selections'] = items
        .map((item) => {
          const id = item.id ?? item.name;
          const levelId = (item.levelId ??
            item.selectionListId ??
            selectionListIds[0]) as TreeFilter['selections'][0]['levelId'];
          return selectionListIds.includes(levelId)
            ? { id, name: item.name, levelId }
            : null;
        })
        .filter((s): s is TreeSelection => s !== null);
      if (selections.length > 0) {
        segmentations[key] = {
          ...definition,
          id: mapping.filterId,
          name: definition.name,
          type: FilterType.TREE,
          selectionListIds,
          selections,
          isUserSubmitted: true,
        } as TreeFilter;
      }
    } else {
      const definition = getFilterDefinition(mapping.filterId);
      if (!definition || definition.type !== FilterType.LIST) continue;
      const selectionListId = (definition as ListFilter).selectionListId;
      const listSelections = items.map((item) => ({
        id: item.id ?? item.name,
        name: item.name,
      }));
      if (listSelections.length > 0) {
        segmentations[key] = {
          ...definition,
          id: mapping.filterId,
          name: definition.name,
          type: FilterType.LIST,
          selectionListId,
          selections: listSelections,
          isUserSubmitted: true,
        } as ListFilter;
      }
    }
  }

  return segmentations;
}

function buildRangeFilterFromSegmentItem({
  filterId,
  item,
  isRateFilter,
}: {
  filterId: FilterId;
  item: SegmentItem;
  isRateFilter: boolean;
}): RangeFilter {
  const definition = getFilterDefinition(filterId);
  const base =
    definition && definition.type === FilterType.RANGE
      ? (definition as RangeFilter)
      : ({
          id: filterId,
          name: String(filterId),
          type: FilterType.RANGE,
        } as RangeFilter);

  let min: number | undefined =
    item.start_value != null ? item.start_value : undefined;
  let max: number | undefined =
    item.end_value != null ? item.end_value : undefined;
  if (isRateFilter && (min !== undefined || max !== undefined)) {
    min = min !== undefined ? min * 100 : undefined;
    max = max !== undefined ? max * 100 : undefined;
  }

  return {
    ...base,
    id: filterId,
    min,
    max,
    isUserSubmitted: true,
  };
}

type SegmentsMap = CompanyDiscoveryAiOutput['filters']['segments'];
const SEGMENT_FIELDS: (keyof SegmentsMap)[] = [
  'headcount',
  'inflow',
  'outflow',
  'hiring',
  'attrition',
  'growth',
  'growth_yoy',
  'salary',
  'tenure',
];

/**
 * Converts CompanyDiscoveryAiOutput segments (start_value/end_value + resolved filter)
 * to SegmentedFilter[] or MultiFilter<SegmentedFilter>.
 */
export const transformSegments = (
  segments: CompanyDiscoveryAiOutput['filters']['segments']
): Filter[] => {
  const result: Filter[] = [];

  if (!segments || typeof segments !== 'object') return result;

  for (const segmentField of SEGMENT_FIELDS) {
    const selectors = segments[segmentField];
    const filterId = getFilterIdForSegmentField(segmentField);
    if (!filterId) continue;

    const arr = Array.isArray(selectors) ? selectors : [];
    if (arr.length === 0) continue;

    const isRateFilter = RATE_SEGMENT_FILTER_IDS.has(filterId);
    const segmentedInstances: SegmentedFilter<RangeFilter>[] = arr.map(
      (item: SegmentItem) => {
        const wrappedFilter = buildRangeFilterFromSegmentItem({
          filterId,
          item,
          isRateFilter,
        });
        const segmentations = buildSegmentationsFromResolvedFilter(item.filter);
        const definition = getFilterDefinition(filterId);
        const name =
          definition?.type === FilterType.RANGE
            ? (definition as RangeFilter).name
            : String(filterId);
        return {
          id: filterId,
          name,
          type: FilterType.SEGMENTED,
          wrappedFilter,
          segmentations:
            Object.keys(segmentations).length > 0 ? segmentations : undefined,
          isUserSubmitted: true,
          isVisible: true,
        } as SegmentedFilter<RangeFilter>;
      }
    );

    if (segmentedInstances.length === 0) continue;

    const multiFilterId = (() => {
      switch (filterId) {
        case FilterId.ATTRITION_RATE:
          return FilterId.MULTI_ATTRITION_RATE;
        case FilterId.GROWTH_RATE:
          return FilterId.MULTI_GROWTH_RATE;
        case FilterId.HIRING_RATE:
          return FilterId.MULTI_HIRING_RATE;
        case FilterId.HEADCOUNT:
          return FilterId.MULTI_HEADCOUNT;
        case FilterId.INFLOW:
          return FilterId.MULTI_INFLOW;
        case FilterId.OUTFLOW:
          return FilterId.MULTI_OUTFLOW;
        case FilterId.SALARY:
          return FilterId.MULTI_SALARY;
        case FilterId.TENURE:
          return FilterId.MULTI_TENURE;
        default:
          return filterId;
      }
    })();
    const multiDef = getFilterDefinition(multiFilterId);
    result.push({
      ...multiDef,
      id: multiFilterId,
      name: multiDef?.name ?? String(multiFilterId),
      type: FilterType.MULTI,
      wrappedFilterId: filterId,
      instances: segmentedInstances,
      isUserSubmitted: true,
    } as Filter);
  }
  return result;
};
